import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mic, Power } from "lucide-react";
import { useVoice } from "../context/VoiceContext";
import { AiOrb } from "./AiOrb";

const POS_KEY = "sara_floating_orb_pos";
const SIZE = 76;

function isHidden(p) {
  return p === "/" || p === "/auth" || p === "/tv" || p.startsWith("/payment");
}

export function FloatingVoiceOrb() {
  const { pathname } = useLocation();
  const voice = useVoice();
  const [pos, setPos] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(POS_KEY));
      if (s && typeof s.x === "number" && typeof s.y === "number") return s;
    } catch (_) {}
    return null; // null → default bottom-right
  });
  const dragRef = useRef(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (pos) localStorage.setItem(POS_KEY, JSON.stringify(pos));
  }, [pos]);

  if (!voice || isHidden(pathname)) return null;

  const { globalActive, activateGlobal, deactivateGlobal, subtitle, subtitleRole, saraState, micLevelRef, speakLevelRef } = voice;

  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
    : { right: 18, bottom: 18 };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    movedRef.current = false;
    const rect = dragRef.current.getBoundingClientRect();
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const dx = Math.abs(e.clientX - (offsetRef.current.x + (pos?.x ?? 0)));
    movedRef.current = true;
    const x = Math.max(6, Math.min(window.innerWidth - SIZE - 6, e.clientX - offsetRef.current.x));
    const y = Math.max(6, Math.min(window.innerHeight - SIZE - 6, e.clientY - offsetRef.current.y));
    void dx;
    setPos({ x, y });
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    // tiny drags count as a tap
    setTimeout(() => { movedRef.current = false; }, 50);
  };
  const onTap = () => {
    if (movedRef.current) return; // it was a drag, not a tap
    if (!globalActive) activateGlobal();
  };

  const ringColor =
    saraState === "listening" ? "#00ffaa" : saraState === "thinking" ? "#ffaa00" : "var(--accent)";

  return (
    <div
      ref={dragRef}
      data-testid="floating-voice-orb"
      style={{ position: "fixed", zIndex: 60, touchAction: "none", ...style }}
      className="flex flex-col items-end select-none"
    >
      {globalActive && subtitle && (
        <div
          data-testid="floating-voice-subtitle"
          className="mb-2 max-w-[62vw] sm:max-w-[280px] rounded-xl border border-[var(--border-hud)] bg-[rgba(4,10,22,0.92)] px-3 py-2 shadow-[var(--accent-glow)] backdrop-blur-md"
        >
          <p className={`font-mono-hud text-[11px] leading-snug ${subtitleRole === "user" ? "text-[#8b949e]" : "accent-text"}`}>
            {subtitleRole === "user" ? "« " : ""}{subtitle}{subtitleRole === "user" ? " »" : ""}
          </p>
        </div>
      )}

      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <button
          type="button"
          data-testid="floating-voice-activate"
          aria-label={globalActive ? "Sara à l'écoute" : "Activer Sara"}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onClick={onTap}
          className="relative h-full w-full cursor-grab active:cursor-grabbing rounded-full"
          style={{ boxShadow: `0 0 0 2px ${ringColor}33, 0 0 22px 2px ${ringColor}55` }}
        >
          <AiOrb
            state={globalActive ? saraState : "idle"}
            micLevelRef={micLevelRef}
            speakLevelRef={speakLevelRef}
            size={SIZE}
          />
          {!globalActive && (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-full border border-[var(--accent)] opacity-60 animate-ping" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <Mic className="h-5 w-5 accent-text" />
              </span>
            </>
          )}
        </button>

        {globalActive && (
          <button
            type="button"
            data-testid="floating-voice-power"
            aria-label="Couper Sara"
            onClick={deactivateGlobal}
            className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--border-hud)] bg-[rgba(4,10,22,0.95)] text-[#8b949e] transition-colors hover:text-white hover:border-[var(--accent)]"
          >
            <Power className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
